import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Receipt, ChevronLeft, CheckCircle } from 'lucide-react';

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>Refund Policy - Sortio</title>
        <meta name="description" content="Sortio refund policy: monthly and annual subscriptions, Mac App Store purchases, EU/UK rights, and how to request a refund." />
      </Helmet>

      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Receipt className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Refund <span className="apple-gradient-text">Policy</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-2xl">
            How refunds work for Sortio subscriptions, including EU/UK statutory rights.
          </p>
        </div>

        <div className="apple-panel p-8 md:p-12 rounded-apple backdrop-blur-sm mb-12">
          <div className="space-y-10 text-apple-300 leading-relaxed">
            <p className="text-sm text-apple-400"><em>Last updated: 17 September 2026</em></p>

            <p>
              This policy applies to Sortio subscriptions purchased directly from <strong>CMG Labs, LLC</strong>, a California limited liability company (California entity number B20250075999) with its principal place of business at 3790 El Camino Real, Unit 593, Palo Alto, CA 94306 ("CMG Labs", "we", "us", or "our"), which provides the Sortio application and services. It forms part of our{' '}
              <Link to="/terms-and-conditions" className="text-sortio-primary hover:text-sortio-primary/80">Terms and Conditions</Link>. The 17 September 2026 update identifies CMG Labs, LLC as the company behind Sortio; it is a clarification only and changed no refund right or time limit described below.
            </p>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Monthly subscriptions</h2>
              <p>
                Monthly Pro subscriptions renew automatically every month until you cancel. Outside the withdrawal window described below, we do not refund partial months. If you cancel, your subscription remains active through the end of the current billing period and is not renewed thereafter.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Annual subscriptions</h2>
              <p>
                Annual Pro subscriptions are refundable on a pro-rata basis if you request a refund within 14 days of the initial purchase or renewal date. After 14 days, annual subscriptions follow the same partial-period rule as monthly subscriptions.
              </p>
              <p>
                Annual renewals receive a pre-renewal reminder by email approximately 30 days before the renewal date, in compliance with the California Automatic Renewal Law (Business and Professions Code 17600 et seq.).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">EU and UK right of withdrawal</h2>
              <p>
                If you are a consumer in the EU or UK, you may withdraw from any Sortio subscription, monthly or annual, within 14 days of the initial purchase or renewal date, under the EU Consumer Rights Directive (Directive 2011/83/EU) and the UK Consumer Contracts Regulations 2013. This right applies regardless of the billing interval and is separate from the discretionary refunds described elsewhere on this page.
              </p>
              <p>
                To withdraw, email marcus@getsortio.com from the address on your account. No particular form of words is required; telling us you want to withdraw is enough. We refund through the original payment method.
              </p>
              <p>
                Because Sortio is usable the moment you subscribe, we ask you at checkout to request that we begin providing it immediately. Making that request does not remove your withdrawal right. If you withdraw within the 14 days, we refund your payment and may retain only an amount proportionate to the period you had access. If we did not obtain that request from you, we refund in full.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Surprise renewals and goodwill refunds</h2>
              <p>
                If you were not aware that your subscription renewed, contact us within 30 days of the renewal charge. We will, in good faith, refund the surprise renewal and cancel future renewals. Goodwill refunds are at our discretion outside the windows described above.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Mac App Store and Apple in-app purchases</h2>
              <p>
                Purchases made through the Mac App Store or any Apple in-app purchase flow are handled by Apple. Request refunds through{' '}
                <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer" className="text-sortio-primary hover:text-sortio-primary/80">reportaproblem.apple.com</a>
                . We cannot issue refunds for purchases we did not collect.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">How to request a refund</h2>
              <ul className="space-y-2">
                {[
                  'Email marcus@getsortio.com from the email address linked to your account.',
                  'Include your account email, the transaction date, the approximate amount, and a short note describing the request.',
                  'We respond within 7 business days. Approved refunds are issued by CMG Labs, LLC through the original payment method by Stripe and may take 5 to 10 additional business days to appear, depending on your bank.',
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Cancellation</h2>
              <p>
                You can cancel anytime in the app at <strong>Settings, Subscription</strong>, or through the Stripe Customer Portal (a link is emailed with every receipt). Cancellation stops future renewals; it is separate from a refund request.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Disputes</h2>
              <p>
                If you disagree with our refund decision, you may pursue the dispute resolution options described in our{' '}
                <Link to="/terms-and-conditions" className="text-sortio-primary hover:text-sortio-primary/80">Terms and Conditions</Link>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
